import { AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import { DataTable, DataTableColumn } from '../../../components/DataTable/DataTable';
import { ModelRunControl, ModelSelection } from '../../../components/ModelRunControl';
import { Race, RaceStatus, ScratchedSlot } from '../../../queries/meetingRaces';
import { formatRaceTime, getRaceStatusBadge, RACE_STATUS_BADGE_CLASS } from './race-drilldown-helpers';

// Prominent (not buried) flag for which race needs a re-run: total scratched
// tips across EVERY slot, plus which slot(s) they're in (e.g. "2 scratched
// (T1, T3)"), so scanning the table tells you exactly what to fix.
function ScratchedBadge({ slots }: { slots: ScratchedSlot[] | null | undefined }) {
  if (!slots || slots.length === 0) return null;
  const total = slots.reduce((sum, s) => sum + s.count, 0);
  const slotList = slots.map((s) => `T${s.analysis_type_id}`).join(', ');
  return (
    <span
      className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-950/40 dark:text-amber-400"
      title={slots.map((s) => `T${s.analysis_type_id}: ${s.count} scratched`).join(' · ')}
    >
      <AlertTriangle className="h-3 w-3 shrink-0" />
      {total} scratched ({slotList})
    </span>
  );
}

function StatusBadge({ status }: { status: RaceStatus | undefined }) {
  const badge = getRaceStatusBadge(status);
  return (
    <div className="flex flex-col items-end gap-1 @2xl:items-start">
      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${RACE_STATUS_BADGE_CLASS[badge.kind]}`}>
        {badge.label}
      </span>
      <ScratchedBadge slots={status?.scratched_by_slot} />
    </div>
  );
}

interface RacesTableProps {
  races: Race[];
  raceStatus: Map<number, RaceStatus>;
  trackId: string;
  meetingDate: string;
  sharedSelection: ModelSelection;
  raceOverrides: Record<number, ModelSelection>;
  onOverride: (raceNumber: number, modelId: number, slot: number) => void;
  onRequeue: (raceId: string) => void;
  requeuingRaceId: string | null;
}

export function RacesTable({
  races,
  raceStatus,
  trackId,
  meetingDate,
  sharedSelection,
  raceOverrides,
  onOverride,
  onRequeue,
  requeuingRaceId,
}: RacesTableProps) {
  const columns: DataTableColumn<Race>[] = [
    {
      key: 'race_number',
      header: 'Race #',
      priority: true,
      render: (race) => <span className="font-medium">{race.race_number}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      priority: true,
      render: (race) => <StatusBadge status={raceStatus.get(race.race_number)} />,
    },
    {
      key: 'race_name',
      header: 'Name',
      priority: false,
      render: (race) => race.race_name ?? '—',
    },
    {
      key: 'distance',
      header: 'Distance',
      priority: false,
      render: (race) => (race.distance ? `${race.distance}m` : '—'),
    },
    {
      key: 'class',
      header: 'Class',
      priority: false,
      render: (race) => race.class ?? '—',
    },
    {
      key: 'race_time',
      header: 'Time',
      priority: false,
      render: (race) => formatRaceTime(race.race_time),
    },
    {
      key: 'condition',
      header: 'Condition',
      priority: false,
      render: (race) => race.track_condition ?? '—',
    },
    {
      key: 'actions',
      header: 'Actions',
      priority: true,
      alignEnd: true,
      render: (race) => {
        const isRequeuing = requeuingRaceId === race.race_id;
        return (
          <div className="flex flex-wrap items-center justify-end gap-2">
            <ModelRunControl
              trackId={trackId}
              meetingDate={meetingDate}
              raceNumber={race.race_number}
              selection={raceOverrides[race.race_number] ?? sharedSelection}
              onSelectionChange={(modelId, slot) => onOverride(race.race_number, modelId, slot)}
            />
            <button
              type="button"
              onClick={() => onRequeue(race.race_id)}
              disabled={isRequeuing}
              title="Re-download race form"
              className="inline-flex h-9 items-center justify-center rounded-md bg-amber-50 px-3 text-amber-700 hover:bg-amber-100 disabled:opacity-50 dark:bg-amber-950/30 dark:text-amber-400"
            >
              {isRequeuing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={races}
      getRowKey={(race) => race.race_id}
      emptyMessage="No races found for this meeting."
    />
  );
}
